/**
 * SweepstakesDataMapper.js
 * Transforms API response structure to component-friendly format
 */

/**
 * Calculate status badge text from progress
 * @param {number} completed - Number of completed steps
 * @param {number} total - Total number of steps
 * @param {boolean} isOptedIn - Whether user is opted in
 * @returns {string} Status text for badge
 */
export const calculateStatus = (completed = 0, total = 0, isOptedIn = false) => {
  if (!isOptedIn || completed === 0) return 'New';
  return `${completed} of ${total} Completed`;
};

/**
 * Map API task to component step format
 * @param {Object} task - Task from API response
 * @param {string} globalIcon - Global step tracker icon URL
 * @returns {Object} Component-friendly step object
 */
const mapTaskToStep = (task, globalIcon) => {
  if (!task) return null;

  return {
    id: task.customerTaskId || task.name,
    title: task.name,
    subtitle: task.description || null,
    description: task.description || null,
    iconUrl: globalIcon || 'https://placehold.co/32x32/000000/FFFFFF?text=%23',
    completed: task.status === 'COMPLETED',
    instructions: task.description || null,
    ctaTitle: task.name || null,
    eventBusTrigger: task.eventBusTrigger || null,
  };
};

/**
 * Map API response to SweepstakesEntryCard props
 * @param {Object} apiResponse - Raw API response
 * @returns {Object} Component-friendly data structure
 */
export const mapSweepstakesData = (apiResponse) => {
  if (!apiResponse) return null;

  const {
    challengeId,
    customerChallengeId,
    name,
    description,
    imageUrl,
    challengeEndDate,
    termsAndConditions,
    optedStatus,
    challengeStepsCompleted,
    challengeStepsTotal,
    tasks,
    challengeMetaData,
  } = apiResponse;

  // Extract metadata fields
  const metadata = challengeMetaData || {};
  const isOptedIn = optedStatus === 'OPTED_IN';

  // Map tasks to steps with null checking
  const mappedSteps = tasks && Array.isArray(tasks)
    ? tasks.map((task) => mapTaskToStep(task, metadata.stepTrackerIcon)).filter(Boolean)
    : [];

  return {
    // Core identifiers
    id: challengeId || customerChallengeId,
    customerChallengeId,

    // Display content
    title: name,
    description: metadata.sweepstakesCard_description || description,
    logo: imageUrl,
    heroImage: metadata.drawerBannerImage,

    // Status and progress
    status: calculateStatus(challengeStepsCompleted, challengeStepsTotal, isOptedIn),
    isOptedIn,
    completedSteps: challengeStepsCompleted || 0,
    totalSteps: challengeStepsTotal || 0,

    // Date information
    expirationDate: challengeEndDate,

    // Bonus entry fields
    bonusEntryEligible: metadata.bonusEntryEligible === 'true',
    messageComplete: metadata.bonusEntry_postText,
    messageNotComplete: metadata.bonusEntry_preText,
    bonusEntryImage: metadata.bonusEntry_img,

    // Steps/tasks
    steps: mappedSteps,
    stepTrackerIcon: metadata.stepTrackerIcon,

    // Terms and drawer content
    termsAndConditions,

    // Opt-in drawer content
    preOptInDrawerTitle: metadata.preOptInDrawer_title,
    preOptInDrawerDescription: metadata.preOptInDrawer_description,
    preOptInDrawerCta: metadata.preOptInDrawer_cta,
    preOptInDrawerCheckbox1: metadata.preOptInDrawer_checkbox_1,
    preOptInDrawerCheckbox2: metadata.preOptInDrawer_checkbox_2,
    preOptInDrawerCheckbox3: metadata.preOptInDrawer_checkbox_3,

    // Details drawer content
    stepTrackerLearnMoreText: metadata.stepTracker_learnMore_text || metadata.stepTracker_leanrMore_text,
    stepTrackerPostOptInDescription: metadata.stepTracker_postOptIn_preRequistie_drawer_description,
    allEntriesCompletedText: metadata.all_entries_completed_text,

    // Additional metadata
    sweepstakesFaqs: metadata['Sweeps FAQ\'s'],
    welcomeCardTitle: metadata.welcomeCard_title,

    // CTA information (calculated)
    ctaTitle: isOptedIn ? null : (metadata.preOptInDrawer_cta || 'Take Survey'),
    ctaHref: null,
    ctaOnclick: null,
  };
};

/**
 * Map multiple sweepstakes from API response
 * @param {Object} apiResponse - API response with challenges array
 * @returns {Array} Array of mapped sweepstakes data
 */
export const mapSweepstakesArray = (apiResponse) => {
  if (!apiResponse || !apiResponse.challenges) return [];

  return apiResponse.challenges
    .map((challenge) => mapSweepstakesData(challenge))
    .filter(Boolean);
};

/**
 * Get first active sweepstakes from API response
 * @param {Object} apiResponse - API response with challenges array
 * @returns {Object|null} First active sweepstakes or null
 */
export const getFirstActiveSweepstakes = (apiResponse) => {
  const mapped = mapSweepstakesArray(apiResponse);
  return mapped.length > 0 ? mapped[0] : null;
};
